import React, {useMemo, useRef} from "react";
import {LoggedUIEvent, User} from "fearless-common";
import {AxisOptions, Chart} from "react-charts";
import moment from "moment";
import useGet from "../../hooks/useGet";
import {backendEndpoints} from "../../../libs/backendEndpoints";
import {UserSerie} from "react-charts/types/types";
import ResizableBox from "../graphs/ResizableBox";

const databaseFormat = 'YYYY-MM-DD HH:mm:ss';

type Entry = { date: string; count: number };

export default function UserRecentActivity({ userId }: { userId: User['id']}) {
  const { current: date } = useRef(`>=${moment().subtract(2, 'weeks').format(databaseFormat)}`);
  const { data: apiData } = useGet<LoggedUIEvent[]>({
    endpoint: backendEndpoints.getLoggedUiEvents,
    params: {
      user_id: userId,
      created_at: date,
    }
  });

  const data: UserSerie<Entry>[] = useMemo(() => {
    const countsByDate: Record<Entry['date'], Entry['count']> = {};
    if (Array.isArray(apiData)) {
      apiData.forEach(({ date}) => {
        if (!countsByDate.hasOwnProperty(date)) {
          countsByDate[date] = 0;
        }
        countsByDate[date] += 1;
      });
    }
    return apiData ? [{
      label: 'Activity',
      data: Object.keys(countsByDate).map(date => ({ date, count: countsByDate[date] })),
    }] : [];
  }, [apiData]);

  const primaryAxis = useMemo(
    (): AxisOptions<Entry> => ({
      getValue: datum => new Date(datum.date),
      scaleType: 'time',
    }),
    []
  );

  const secondaryAxes = React.useMemo(
    (): AxisOptions<Entry>[] => [
      {
        getValue: entry => entry.count,
        scaleType: 'linear',
      },
    ],
    []
  )

  return (
    <ResizableBox height={200} width={300}>
      <Chart
        options={{
          data,
          primaryAxis,
          secondaryAxes,
        }}
      />
    </ResizableBox>
  );
}
